'use client';

import { Certificate, CourseCategory } from '@/types';
import Button from '@components/Button';
import MultiCheckbox from '@components/Form/MultiCheckbox';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { SubmitEvent, useId } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import SeminarSearchFilter from './SeminarSearchFilter';
import Form from 'next/form';

import UserScreenIcon from '@/icons/user-screen.svg';
import SearchIcon from '@/icons/search.svg';
import Link from 'next/link';
import { removeBaseUrl } from '@utils/url';
import clsx from 'clsx';

const ENTRY_LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Fortgeschritten' },
  { value: 'expert', label: 'Experte' },
];

type Props = {
  categories: CourseCategory[];
  defaultCategory?: number;
  defaultCertificate?: number;
  certificates: Certificate[];
  parentUrl?: string;
  className?: string;
  actionUrl?: string;
};

function SeminarSearchForm(props: Props) {
  const { categories, actionUrl = '', defaultCategory, defaultCertificate, certificates, parentUrl, className } = props;
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const id = useId();

  function handleChange(name: string, value: string) {
    const params = new URLSearchParams(searchParams);

    if (!value || value === 'empty') {
      params.delete(name);
    } else {
      params.set(name, value);
    }

    params.delete('page');

    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  }

  function handleMultiChange(name: string, value: string) {
    const params = new URLSearchParams(searchParams);

    if (params.getAll(name).includes(value)) {
      params.delete(name, value);
    } else {
      params.append(name, value);
    }

    params.delete('page');

    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  }

  function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    // router.replace(`${pathname}?${searchParams.toString()}`, { scroll: false });
  }

  const debouncedHandleChange = useDebouncedCallback(handleChange, 300);

  return (
    <Form action={actionUrl} onSubmit={handleSubmit} className={clsx('seminar-search-form', className)}>
      <div className='seminar-search-form__search-field'>
        <input
          className='seminar-search-form__search-input'
          type='search'
          name='query'
          placeholder='Weiterbildung suchen'
          aria-label='Suchbegriff eingeben'
          onChange={e => debouncedHandleChange(e.target.name, e.target.value)}
          defaultValue={searchParams.get('query')?.toString()}
        />
        <SearchIcon className='seminar-search-form__search-icon' width={19} height={19} />
      </div>
      <div className='seminar-search-form__filters'>
        {!defaultCategory ? (
          <SeminarSearchFilter
            label='Themenbereich'
            id={`${id}-category`}
            options={categories.map(category => ({
              label: category.course_category_desc,
              value: category.course_category_id.toString(),
            }))}
            name='category'
            defaultValue={searchParams.get('category')?.toString()}
            handleChange={value => handleChange('category', value)}
            emptyText='Alle Themenbereiche'
          />
        ) : null}
        <SeminarSearchFilter
          label='Zielgruppe'
          id={`${id}-entry_level`}
          options={ENTRY_LEVELS}
          name='entry_level'
          defaultValue={searchParams.get('entry_level')?.toString()}
          handleChange={value => handleChange('entry_level', value)}
          emptyText='Alle Zielgruppen'
        />
        {!defaultCertificate ? (
          <SeminarSearchFilter
            label='Zertifikat'
            id={`${id}-certificate`}
            options={certificates.map(certificate => ({
              label: certificate.title,
              value: certificate.slug,
            }))}
            handleChange={value => handleChange('certificate', value)}
            name='certificate'
            defaultValue={searchParams.get('certificate')?.toString()}
            emptyText='Alle Zertifikate'
          />
        ) : null}
      </div>
      <MultiCheckbox
        className='seminar-search-form__fieldset'
        legend='Art des Kurses'
        name='course_type'
        handleChange={handleMultiChange}
        items={[
          {
            label: 'Online-Seminare',
            value: 'ONLINE',
            defaultChecked: searchParams.getAll('course_type').includes('ONLINE'),
          },
          {
            label: 'Präsenzschulungen',
            value: 'IN_PERSON',
            defaultChecked: searchParams.getAll('course_type').includes('IN_PERSON'),
          },
          {
            label: 'Inhouse-Schulungen',
            value: 'IN_HOUSE',
            defaultChecked: searchParams.getAll('course_type').includes('IN_HOUSE'),
          },
        ]}
      />
      <div className='seminar-search-form__buttons'>
        {defaultCategory && parentUrl ? (
          <Link
            className='seminar-search-form__parent-link button button--ghost button--white'
            href={removeBaseUrl(parentUrl)}
          >
            Anderen Themenbereich auswählen
          </Link>
        ) : null}
        {defaultCertificate && parentUrl ? (
          <Link
            className='seminar-search-form__parent-link button button--ghost button--white'
            href={removeBaseUrl(parentUrl)}
          >
            Andere Zertifizierung auswählen
          </Link>
        ) : null}
        <Button type='submit' primary className='seminar-search-form__submit-button'>
          <UserScreenIcon width={24} height={24} />
          Seminar suchen
        </Button>
      </div>
    </Form>
  );
}

export default SeminarSearchForm;
